import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
} from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import { toast, ToastContainer } from "react-toastify";
import { DataTable, TableColumnDef } from "../../components/DataTable";
import { ApiGet } from "../../helpers/API/ApiData";
import { PageHeader } from "../../components/PageHeader";

type MobileUserRow = {
  _id: string;
  firstName?: string;
  lastName?: string;
  emailId?: string;
  phoneNo?: string;
  status?: { name?: string };
};

const WelcomeEmailsPage = () => {
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);
  const [countPerPage, setCountPerPage] = useState(10);
  const [users, setUsers] = useState<MobileUserRow[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [search, setSearch] = useState("");

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<MobileUserRow | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, [page, countPerPage, search]);

  const fetchUsers = async () => {
    setLoadingData(true);
    try {
      const res: any = await ApiGet(
        `user/find-for-admin?letter=${search}&page=${page}&limit=${countPerPage}`
      );
      setUsers(res?.data?.data ?? []);
      setCount(res?.data?.count ?? 0);
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Etwas ist schief gelaufen. Bitte versuche es erneut"
      );
    } finally {
      setLoadingData(false);
    }
  };

  const openConfirm = (row: MobileUserRow) => {
    setConfirmTarget(row);
    setConfirmOpen(true);
  };

  const closeConfirm = () => {
    if (submitting) return;
    setConfirmOpen(false);
    setConfirmTarget(null);
  };

  const confirmResend = async () => {
    if (!confirmTarget?._id) return;
    setSubmitting(true);
    try {
      const res: any = await ApiGet(
        `user/resend-welcome-email?id=${confirmTarget._id}`
      );
      if (res?.status === 200) {
        toast.success("Willkommens-E-Mail wurde erfolgreich erneut gesendet");
        setConfirmOpen(false);
        setConfirmTarget(null);
      } else {
        toast.error(
          res?.data?.message || "Fehler beim Senden der E-Mail"
        );
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ||
          "Fehler beim Senden der E-Mail. Bitte versuche es erneut"
      );
    } finally {
      setSubmitting(false);
    }
  };

  const columns: TableColumnDef[] = [
    {
      header: "Vorname",
      render: (row: any) => row?.firstName || "-",
      getValue: (row: any) => row?.firstName,
    },
    {
      header: "Nachname",
      render: (row: any) => row?.lastName || "-",
      getValue: (row: any) => row?.lastName,
    },
    {
      header: "E-Mail",
      render: (row: any) => row?.emailId || "-",
      getValue: (row: any) => row?.emailId,
    },
    {
      header: "Telefon",
      render: (row: any) => row?.phoneNo || "-",
      getValue: (row: any) => row?.phoneNo,
    },
    {
      header: "Status",
      render: (row: any) => (
        <Typography
          variant="caption"
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            color: "white",
            bgcolor:
              row?.status?.name === "active" ? "success.main" : "error.main",
          }}
        >
          {row?.status?.name === "active" ? "Aktiv" : "Inaktiv"}
        </Typography>
      ),
    },
    {
      header: "Aktion",
      render: (row: any) => (
        <Tooltip title="Willkommens-E-Mail erneut senden" arrow>
          <span>
            <Button
              variant="contained"
              color="info"
              size="small"
              startIcon={<EmailIcon />}
              disabled={!row?.emailId}
              onClick={(e) => {
                e.stopPropagation();
                openConfirm(row);
              }}
              sx={{ whiteSpace: "nowrap" }}
            >
              E-Mail senden
            </Button>
          </span>
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <ToastContainer />
      <Paper sx={{ p: 1 }}>
        <Box sx={{ p: 2, mb: 2 }}>
          <PageHeader
            title="Willkommens-E-Mails"
            onSearch={(val) => {
              setSearch(val);
              setPage(1);
            }}
          />

          {loadingData ? (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 2.5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataTable
              rows={users}
              columns={columns}
              page={page}
              count={count}
              rowsPerPage={countPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={setCountPerPage}
            />
          )}
        </Box>
      </Paper>

      <Dialog
        open={confirmOpen}
        onClose={closeConfirm}
        aria-labelledby="resend-welcome-confirm-title"
      >
        <DialogTitle id="resend-welcome-confirm-title">
          Willkommens-E-Mail erneut senden?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Es wird ein neues Passwort generiert und an{" "}
            <strong>{confirmTarget?.emailId || "den Benutzer"}</strong>{" "}
            gesendet. <strong>Das aktuelle Passwort wird ungültig</strong> —
            falls die Person bereits in der App angemeldet ist, muss sie sich
            nach dieser Aktion erneut mit dem neuen Passwort anmelden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm} disabled={submitting}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmResend}
            color="info"
            variant="contained"
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <EmailIcon />
              )
            }
          >
            {submitting ? "Wird gesendet…" : "Senden"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default WelcomeEmailsPage;
